import { Backdrop, Box, Modal, Slide } from "@mui/material";
import type { ReactNode } from "react";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

/** Content-fit width presets (px on sm+; full viewport on xs). */
export const SLIDE_OVER_SIZE_PX = {
  sm: 480,
  md: 640,
  lg: 800,
  xl: 1040,
} as const;

export type SlideOverSize = keyof typeof SLIDE_OVER_SIZE_PX;

type Props = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Fixed pixel width — prefer `size` for content-fit presets. */
  width?: number;
  /** Content-fit preset; ignored when `fullscreen` or `fullWidth`. */
  size?: SlideOverSize;
  /** @deprecated Use `fullscreen`. */
  fullWidth?: boolean;
  /** Expand panel across the viewport (same as fullscreen toggle on). */
  fullscreen?: boolean;
  backgroundColor?: string;
};

/**
 * Right-side slide-over panel. Uses Modal (not MuiDrawer) so global sidenav Drawer
 * theme overrides do not force left:0 / 250px width on every drawer in the app.
 *
 * Prefer {@link AppSlideOver} for create/edit CRUD chrome (title, fullscreen, actions).
 */
export default function SlideOverPanel({
  open,
  onClose,
  children,
  width,
  size = "md",
  fullWidth = false,
  fullscreen = false,
  backgroundColor,
}: Props) {
  const custom = useAppCustomStyles();
  const resolvedWidth = width ?? SLIDE_OVER_SIZE_PX[size];
  const expand = fullscreen || fullWidth;
  const panelStyles = expand
    ? custom.components.slideOverPanel.panelFullWidth(backgroundColor)
    : custom.components.slideOverPanel.panel(resolvedWidth, backgroundColor);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableEnforceFocus
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 250,
          sx: custom.components.slideOverPanel.backdrop,
        },
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit timeout={250}>
        <Box role="dialog" aria-modal="true" tabIndex={-1} sx={panelStyles}>
          {children}
        </Box>
      </Slide>
    </Modal>
  );
}
